"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TrendingUp, Search, MapPin, ArrowUpRight, Target, ChartBar as BarChart2, CircleCheck as CheckCircle2, Zap, Building2, Sparkles, Compass } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const sunderlandKeywords = [{
  keyword: "SEO Consultant Sunderland",
  volume: "880/mo",
  intent: "Commercial",
  difficulty: "Medium",
  potential: "+140%"
}, {
  keyword: "Local SEO Wearside",
  volume: "450/mo",
  intent: "Transactional",
  difficulty: "Low",
  potential: "+210%"
}, {
  keyword: "Sunderland B2B Lead Gen",
  volume: "320/mo",
  intent: "High Commercial",
  difficulty: "Low",
  potential: "+180%"
}, {
  keyword: "Ecommerce SEO North East",
  volume: "1,200/mo",
  intent: "Transactional",
  difficulty: "High",
  potential: "+95%"
}, {
  keyword: "Technical SEO Audit Sunderland",
  volume: "210/mo",
  intent: "Direct Hire",
  difficulty: "Low",
  potential: "+250%"
}];
const opportunityPoints = [{
  title: "Low Competitor Authority",
  description: "Most local Sunderland businesses rely on outdated agency retainers without technical schema or AI search readiness. Direct strategy quickly dominates top rankings.",
  icon: Target
}, {
  title: "High Commercial Intent Keywords",
  description: "Wearside search terms carry higher conversion rates than national broad queries. Capturing local intent delivers direct inbound client enquiries.",
  icon: Zap
}, {
  title: "AI Search Gap (GEO & Perplexity)",
  description: "Local competitors have zero visibility in Google AI Overviews or Bing Copilot. I optimize your entity profile so AI engines cite your business directly.",
  icon: Sparkles
}];
const neighboringLocations = [{
  name: "Wakefield",
  path: "/LocalSeoConsultantWakefieldPage",
  region: "West Yorkshire"
}, {
  name: "Derby",
  path: "/LocalSeoConsultantDerbyPage",
  region: "East Midlands"
}, {
  name: "Stoke-on-Trent",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  region: "Staffordshire"
}, {
  name: "Northampton",
  path: "/LocalSeoConsultantNorthamptonPage",
  region: "East Midlands"
}, {
  name: "Milton Keynes",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  region: "Buckinghamshire"
}, {
  name: "National UK Services",
  path: "/NationalSeoPage",
  region: "United Kingdom"
}];
export default function Sunderland() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-item", {
        y: 30
      });
      gsap.to(".reveal-item", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".reveal-item", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="sunderlandseoanalysis" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="reveal-item max-w-3xl mb-12 lg:mb-16" style={{
        opacity: 0
      }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-medium mb-4">
            <MapPin className="size-3.5 text-primary" />
            <span>Sunderland & North East Organic Market Data</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Sunderland SEO Landscape &amp; Search Growth Analysis
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            I continuously track Wearside and North East search behaviour. Here is my live breakdown of high-intent Sunderland keywords, competitor gaps, and immediate organic growth opportunities for your business.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          {}
          <Card className="reveal-item lg:col-span-7 bg-card text-card-foreground border-border shadow-md" style={{
          opacity: 0
        }}>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-serif font-bold flex items-center gap-2">
                    <Search className="size-5 text-primary" />
                    High-Value Sunderland Search Queries
                  </CardTitle>
                  <CardDescription>Verified search trends and commercial intent estimates</CardDescription>
                </div>
                <Badge variant="secondary" className="hidden sm:inline-flex">
                  Updated Quarterly
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border text-muted-foreground font-medium text-xs uppercase tracking-wider">
                    <tr>
                      <th className="py-3 px-2">Keyword Phrase</th>
                      <th className="py-3 px-2">Monthly Vol.</th>
                      <th className="py-3 px-2">Intent</th>
                      <th className="py-3 px-2 text-right">Growth Lead</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {sunderlandKeywords.map((item, i) => <tr key={i} data-index={i} className="hover:bg-muted/40 transition-colors">
                        <td className="py-3.5 px-2 font-medium text-foreground flex items-center gap-2">
                          <CheckCircle2 className="size-4 text-primary shrink-0" />
                          <span>{item.keyword}</span>
                        </td>
                        <td className="py-3.5 px-2 text-muted-foreground">{item.volume}</td>
                        <td className="py-3.5 px-2">
                          <Badge variant="outline" className="text-xs border-primary/20 bg-primary/5 text-primary">
                            {item.intent}
                          </Badge>
                        </td>
                        <td className="py-3.5 px-2 text-right font-semibold text-primary">{item.potential}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-4 rounded-lg bg-muted/60 border border-border flex items-start gap-3">
                <BarChart2 className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <strong className="text-foreground font-medium">My Assessment:</strong> Sunderland businesses that fix core technical SEO debt and optimize for Wearside local entity signals typically see first-page rankings within 90 days.
                </p>
              </div>
            </CardContent>
          </Card>

          {}
          <div className="lg:col-span-5 flex flex-col justify-between gap-6">
            <div className="reveal-item space-y-4" style={{
            opacity: 0
          }}>
              <h3 className="text-2xl font-serif font-bold text-foreground flex items-center gap-2">
                <TrendingUp className="size-6 text-primary" />
                Where Sunderland Competitors Fail
              </h3>
              <p className="text-sm text-muted-foreground">
                You work directly with me — no agency junior or outsourced hand-offs. I uncover structural gaps in your local market that your competitors have completely overlooked.
              </p>
            </div>

            <div className="space-y-4">
              {opportunityPoints.map((point, i) => {
              const IconComponent = point.icon;
              return <Card key={i} data-index={i} className="reveal-item bg-card text-card-foreground border-border shadow-xs hover:border-primary/40 transition-all" style={{
                opacity: 0
              }}>
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                        <IconComponent className="size-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground text-base mb-1">{point.title}</h4>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {point.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>
        </div>

        {}
        <div className="reveal-item bg-primary text-primary-foreground rounded-2xl p-8 sm:p-10 mb-16 shadow-xl relative overflow-hidden" style={{
        opacity: 0
      }}>
          <div className="absolute -right-10 -bottom-10 opacity-10 pointer-events-none">
            <Building2 className="size-80" />
          </div>
          <div className="relative z-10 max-w-3xl">
            <Badge className="bg-accent text-accent-foreground mb-4 border-none font-semibold">
              Personal Guarantee
            </Badge>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold mb-4">
              {"\"I personally craft and execute your Sunderland SEO campaign.\""}
            </h3>
            <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed mb-6">
              Unlike traditional agencies that pass Sunderland accounts to junior staffers, I analyze your technical code, author custom schema, build authoritative local links, and directly manage your campaign for sustainable organic leads.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <Button asChild size="lg" className="bg-background text-foreground hover:bg-background/90 font-medium">
                <Link to="/ContactPage">
                  Book a Free Sunderland SEO Audit
                  <ArrowUpRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/CaseStudiesPage">
                  Explore Verified Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="reveal-item" style={{
        opacity: 0
      }}>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary mb-2">
                <Compass className="size-4" />
                <span>Geographic & Regional Expertise</span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                Local SEO Consulting Across UK Regions
              </h3>
            </div>
            <Button asChild variant="ghost" className="text-primary hover:text-primary/80 self-start md:self-auto">
              <Link to="/LocalSEOPage">
                View All Local SEO Locations
                <ArrowUpRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <Separator className="mb-8" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {neighboringLocations.map((loc, i) => <Card key={i} data-index={i} className="bg-card text-card-foreground border-border shadow-xs hover:shadow-md hover:border-primary/50 transition-all group">
                <CardContent className="p-5 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-muted-foreground block mb-0.5">{loc.region}</span>
                    <h4 className="font-medium text-foreground text-base group-hover:text-primary transition-colors">
                      SEO Consultant {loc.name}
                    </h4>
                  </div>
                  <Button asChild size="icon" variant="secondary" className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                    <Link to={loc.path} aria-label={`View SEO Consulting for ${loc.name}`}>
                      <ArrowUpRight className="size-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </div>
    </section>;
}